import type { RunEventResponse, ScenarioStateResponse, ScheduleAction } from "@/api/types";
import { pickActionFlavor, type ActionOutcomeBand } from "@/content/actionFlavorLines";

/**
 * 매 주차마다 캐릭터가 남긴 짧은 일기를 조건 매트릭스로 자동 생성한다.
 *
 * <p>2026-04-23 E25: "회고 일지" 탭 신설. 런이 끝난 뒤 플레이어가 이번 여정을
 * 되돌아볼 수 있는 서사 스크롤. 백엔드에 일기 테이블을 두지 않고, eventLog +
 * lastAction + scenarioState 에서 결정론으로 재계산한다.
 *
 * <p>각 주차 일기는 3 영역의 조합:
 *   - <strong>action note</strong>: 그 주 어떤 행로에 시간을 썼는가
 *   - <strong>event echo</strong>: 해당 주차 이벤트 1~2개에서 뽑은 여운
 *   - <strong>mood line</strong> (선택): 스탯/자원 큰 변동 때 감정 한 줄
 *
 * <p>철저히 짧게(1~3문장), 서사 톤으로.
 */

export interface DiaryEntry {
    weekNo: number;
    /** 본문 1~3 문장. \n 으로 구분. */
    body: string;
    /** "이번 주를 정의한 태그" — action 또는 이벤트 성격. */
    tag: string;
    /** 강조 컬러 톤 힌트. */
    tone: "steady" | "growth" | "loss" | "decision" | "warmth";
}

export interface DiaryContext {
    characterName: string;
    currentWeek: number;
    eventLog: RunEventResponse[];
    /** 현재 주차의 선택된 action (서버 run.lastAction). 과거 주차는 eventType 으로 유추. */
    currentWeekAction?: ScheduleAction | null;
    scenarioState?: ScenarioStateResponse | null;
}

/**
 * 런 데이터에서 주차별 일기 리스트를 생성한다. 최신 주부터 역순.
 *
 * <p>eventLog 에 등장한 주차를 기준으로 순회하며, 각 주차마다 본문 템플릿을
 * 채운다. 각 주차의 action 은 schedule 이벤트 eventType (SCHEDULE_RESULT_*) 과
 * title 에서 유추. 실패하면 event echo 만으로 구성.
 */
export function buildDiaryEntries(ctx: DiaryContext): DiaryEntry[] {
    const byWeek = new Map<number, RunEventResponse[]>();
    for (const e of ctx.eventLog) {
        const list = byWeek.get(e.weekNo) ?? [];
        list.push(e);
        byWeek.set(e.weekNo, list);
    }
    const weeks = Array.from(byWeek.keys()).sort((a, b) => b - a);
    const entries: DiaryEntry[] = [];
    for (const w of weeks) {
        const weekEvents = byWeek.get(w) ?? [];
        // 과거 주차는 schedule 이벤트 title 에서 action 유추. 현재 주차는 explicit.
        const action = w === ctx.currentWeek
            ? (ctx.currentWeekAction ?? inferActionFromEvents(weekEvents))
            : inferActionFromEvents(weekEvents);
        const entry = composeWeekEntry({
            characterName: ctx.characterName,
            weekNo: w,
            action,
            events: weekEvents,
        });
        if (entry) entries.push(entry);
    }
    return entries;
}

/**
 * schedule 이벤트 title 에서 action 유추. 실패 시 null.
 * 서버 title 형식: "{band prefix} {actionDisplayName}" — 예: "무난한 훈련", "덜컹거린 제작".
 */
function inferActionFromEvents(events: RunEventResponse[]): ScheduleAction | null {
    for (const e of events) {
        if (e.sourceType !== "schedule") continue;
        const title = e.title ?? "";
        if (title.includes("훈련")) return "TRAINING";
        if (title.includes("휴식") || title.includes("쉼") || title.includes("숨을 고르")) return "REST";
        if (title.includes("제작") || title.includes("공방")) return "CRAFTING";
        if (title.includes("외출") || title.includes("바깥")) return "OUTING";
        if (title.includes("연구") || title.includes("책상")) return "RESEARCH";
        if (title.includes("정찰") || title.includes("선답")) return "SCOUTING";
    }
    return null;
}

function composeWeekEntry(args: {
    characterName: string;
    weekNo: number;
    action: ScheduleAction | null;
    events: RunEventResponse[];
}): DiaryEntry | null {
    const { weekNo, action, events } = args;
    if (events.length === 0 && !action) return null;

    // action 이 있고 schedule 이벤트 band 를 읽을 수 있으면 actionFlavorLines 사용.
    // 그 외에는 기본 ACTION_VARIANTS[0] 폴백.
    const band = inferOutcomeBand(events);
    const actionNote = action
        ? (band
            ? pickActionFlavor({ action, band, weekNo })
            : actionSentence(action))
        : null;
    // schedule 이벤트는 이미 flavor 로 소비됐으므로 event echo 에서 제외.
    const nonScheduleEvents = events.filter(e => e.sourceType !== "schedule");
    const eventEchoes = nonScheduleEvents
        .slice(0, 2)
        .map(e => eventEcho(e))
        .filter(Boolean) as string[];
    const moodLine = moodSentence(events);

    const bodyParts: string[] = [];
    if (actionNote) bodyParts.push(actionNote);
    for (const echo of eventEchoes) bodyParts.push(echo);
    if (moodLine) bodyParts.push(moodLine);

    if (bodyParts.length === 0) return null;

    return {
        weekNo,
        body: bodyParts.join(" "),
        tag: resolveTag(action, events),
        tone: resolveTone(action, events),
    };
}

/** schedule 이벤트 eventType "SCHEDULE_RESULT_*" 에서 band 파싱. 없으면 null. */
function inferOutcomeBand(events: RunEventResponse[]): ActionOutcomeBand | null {
    for (const e of events) {
        if (e.sourceType !== "schedule") continue;
        const t = e.eventType ?? "";
        if (t.endsWith("GREAT_SUCCESS")) return "GREAT_SUCCESS";
        if (t.endsWith("SUCCESS")) return "SUCCESS";
        if (t.endsWith("POOR")) return "POOR";
        if (t.endsWith("FAILURE")) return "FAILURE";
        // outcomeBand 필드가 이미 DTO 에 있으면 더 정확:
        if (e.outcomeBand === "GREAT_SUCCESS" || e.outcomeBand === "SUCCESS"
            || e.outcomeBand === "POOR" || e.outcomeBand === "FAILURE") return e.outcomeBand;
    }
    return null;
}

/** 행동 주차 한 줄 — 선택한 행로의 서사 톤. */
function actionSentence(action: ScheduleAction): string {
    // 같은 행동이라도 3~4 배리언트를 놓아 반복을 덜어 낸다. 주차 번호로 셔플.
    const variants = ACTION_VARIANTS[action] ?? DEFAULT_ACTION_VARIANTS;
    return variants[0]; // 간단화: 일단 0번 사용. 나중에 weekNo % len 로 바리에이션.
}

const DEFAULT_ACTION_VARIANTS = ["이 주는 조용히 흘러갔다."];

const ACTION_VARIANTS: Record<ScheduleAction, string[]> = {
    TRAINING: [
        "이번 주는 몸을 단련하는 데 썼다. 근육이 기억할 때까지 같은 동작을 반복했다.",
    ],
    REST: [
        "이번 주는 숨을 골랐다. 어떤 결정도 내리지 않고 나 자신을 먼저 돌보기로 했다.",
    ],
    CRAFTING: [
        "이번 주는 공방에 틀어박혔다. 손끝에서 다듬어진 것들이 내일의 무게를 버텨 줄 것이다.",
    ],
    OUTING: [
        "이번 주는 바깥을 거닐었다. 낯선 얼굴과 오래된 이야기가 장부에 한 줄씩 더해졌다.",
    ],
    RESEARCH: [
        "이번 주는 책상 앞에서 밤을 맞았다. 답이 명확해지기보다는 질문이 더 선명해졌다.",
    ],
    SCOUTING: [
        "이번 주는 먼저 길을 보는 자리에 섰다. 먼저 본다는 건 먼저 위험해지는 일이기도 했다.",
    ],
    SCENARIO_EVENT: [
        "이번 주는 이야기가 먼저 손을 내밀었다. 선택하지 않는 것조차 하나의 선택이 되었다.",
    ],
};

/** 이벤트 1건에서 짧은 여운 한 문장 추출. */
function eventEcho(event: RunEventResponse): string | null {
    const title = event.title?.trim();
    if (!title) return null;
    // title 은 짧은 사건 이름 — 그대로 인용 + 서사 끝맺음
    if (event.sourceType === "combat") {
        return `그 주의 "${title}" 가 남긴 흉터는 아직 온전하지 않다.`;
    }
    if (event.sourceType === "destiny") {
        return `"${title}" 의 결을 함께한 운명이 하나 더 나와 가까워졌다.`;
    }
    if (event.sourceType === "scenario") {
        return `"${title}" 의 장면은 쉬이 잊히지 않을 것이다.`;
    }
    if (event.sourceType === "class") {
        return `"${title}" 을 지나며 나의 영혼 한 자락이 조금 더 선명해졌다.`;
    }
    return `"${title}" 가 이 주의 장부 한 줄에 올랐다.`;
}

/** 이벤트들의 집계 신호에서 감정 한 줄. 없으면 null. */
function moodSentence(events: RunEventResponse[]): string | null {
    if (events.length === 0) return null;

    let statUp = 0, statDown = 0;
    let goldDelta = 0;
    for (const e of events) {
        for (const v of Object.values(e.statDelta ?? {})) {
            if (v > 0) statUp += v;
            else if (v < 0) statDown += v;
        }
        for (const [k, v] of Object.entries(e.resourceDelta ?? {})) {
            if (k === "gold") goldDelta += v;
        }
    }
    if (statUp >= 6 && statDown === 0) {
        return "다음 결투를 앞두고, 손끝에 조금 더 확신이 남았다.";
    }
    if (statDown <= -6) {
        return "오늘은 몸 어딘가에 낯선 결림이 남았다. 다음 주의 판단이 느려질지도 모른다.";
    }
    if (goldDelta >= 60) {
        return "장부의 무게가 조금 더 무거워졌다. 이 무게가 책임이 될지 여유가 될지는 다음 주가 정한다.";
    }
    if (goldDelta <= -40) {
        return "장부가 얇아지는 소리가 오래 귓가에 남았다.";
    }
    return null;
}

function resolveTag(action: ScheduleAction | null, events: RunEventResponse[]): string {
    if (events.some(e => e.sourceType === "scenario")) return "시나리오";
    if (events.some(e => e.sourceType === "combat")) return "전투";
    if (events.some(e => e.sourceType === "destiny")) return "운명";
    if (events.some(e => e.sourceType === "class")) return "영혼";
    if (action) return ACTION_TAG[action] ?? "주간";
    return "주간";
}

const ACTION_TAG: Record<ScheduleAction, string> = {
    TRAINING: "훈련",
    REST: "휴식",
    CRAFTING: "제작",
    OUTING: "외출",
    RESEARCH: "연구",
    SCOUTING: "정찰",
    SCENARIO_EVENT: "이야기",
};

function resolveTone(
    action: ScheduleAction | null,
    events: RunEventResponse[],
): DiaryEntry["tone"] {
    if (events.some(e => e.sourceType === "combat")) return "loss";
    if (events.some(e => e.sourceType === "scenario")) return "decision";
    if (events.some(e => e.sourceType === "destiny")) return "warmth";
    if (action === "REST") return "steady";
    if (action === "TRAINING" || action === "CRAFTING" || action === "RESEARCH") return "growth";
    return "steady";
}
